import { log, logE, logV } from "../../../shared/services/debug-logger";
import { HULL_CLEANING_DASHBOARD_MOCK } from "../mocks/hull-cleaning-dashboard.mock";
import {
  EMPTY_HULL_CLEANING_FILTERS,
  type HullCleaningCase,
  type HullCleaningDashboard,
  type HullCleaningDashboardFilters,
  type HullCleaningDashboardView,
  type HullCleaningFilterSetter,
} from "../models/hull-cleaning-dashboard.models";
import { getHullCleaningDashboard } from "../services/hull-cleaning-dashboard.service";
import {
  filterHullCleaningCases,
  filterHullCleaningDryDocks,
  filterHullCleaningEvents,
  filterHullCleaningVoyages,
  hasVoyageRelationshipFilter,
  keepRowsForFilteredVoyageImos,
} from "../utils/hull-cleaning-dashboard-selectors";
import { hullCleaningMockEnabledAtom } from "./hull-cleaning-dashboard.atoms";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";

// Owns the report bookmark, filters, load lifecycle, and selected request outside the router outlet.
export const useHullCleaningDashboard = (canLoad: boolean) => {
  const useMockData = useAtomValue(hullCleaningMockEnabledAtom);
  const [dashboard, setDashboard] = useState<HullCleaningDashboard | null>(null);
  const [view, setView] = useState<HullCleaningDashboardView>("voyage");
  const [filters, setFilters] = useState<HullCleaningDashboardFilters>(EMPTY_HULL_CLEANING_FILTERS);
  const [selectedCase, setSelectedCase] = useState<HullCleaningCase | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isCurrent = true;

    if (!canLoad) {
      log("STATE - useHullCleaningDashboard - disabled");
      setIsLoading(false);

      return () => {
        isCurrent = false;
      };
    }

    setSelectedCase(null);
    setFilters(EMPTY_HULL_CLEANING_FILTERS);

    if (useMockData) {
      log("STATE - useHullCleaningDashboard - mock applied", { voyages: HULL_CLEANING_DASHBOARD_MOCK.voyageItinerary.length, cases: HULL_CLEANING_DASHBOARD_MOCK.cases.length });
      setDashboard(HULL_CLEANING_DASHBOARD_MOCK);
      setError(null);
      setIsLoading(false);

      return () => {
        isCurrent = false;
      };
    }

    setIsLoading(true);
    log("STATE - useHullCleaningDashboard - loading");
    setError(null);
    getHullCleaningDashboard()
      .then((nextDashboard) => {
        if (isCurrent) {
          setDashboard(nextDashboard);
          log("STATE - useHullCleaningDashboard - applied", { voyages: nextDashboard.voyageItinerary.length, cases: nextDashboard.cases.length });
        } else {
          logV("STATE - useHullCleaningDashboard - stale result");
        }
      })
      .catch((error: unknown) => {
        if (isCurrent) {
          logE(error, { operation: "STATE - useHullCleaningDashboard - failed" });
          setDashboard(null);
          setError("The hull-cleaning dashboard could not be loaded.");
        }
      })
      .finally(() => {
        if (isCurrent) {
          setIsLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [canLoad, useMockData]);

  const filtered = useMemo(() => {
    const voyageItinerary = filterHullCleaningVoyages(dashboard?.voyageItinerary ?? [], filters);
    const voyageImos = new Set(voyageItinerary.map((row) => row.imo).filter((imo): imo is string => imo !== null));
    const restrictToVoyages = hasVoyageRelationshipFilter(filters);

    return {
      voyageItinerary,
      cases: keepRowsForFilteredVoyageImos(
        filterHullCleaningCases(dashboard?.cases ?? [], filters),
        voyageImos,
        restrictToVoyages,
      ),
      dryDocks: keepRowsForFilteredVoyageImos(
        filterHullCleaningDryDocks(dashboard?.dryDocks ?? [], filters),
        voyageImos,
        restrictToVoyages,
      ),
      hullEvents: keepRowsForFilteredVoyageImos(
        filterHullCleaningEvents(dashboard?.hullEvents ?? [], filters),
        voyageImos,
        restrictToVoyages,
      ),
    };
  }, [dashboard, filters]);

  const caseImos = useMemo(
    () => new Set(
      (dashboard?.cases ?? [])
        .filter((row) => row.status && ["OPEN", "PLANNED"].includes(row.status.toUpperCase()))
        .map((row) => row.imo)
        .filter((imo): imo is string => imo !== null),
    ),
    [dashboard],
  );

  const setFilter: HullCleaningFilterSetter = (name, value) =>
    setFilters((current) => ({ ...current, [name]: value }));

  const changeView = useCallback((nextView: Parameters<typeof setView>[0]) => {
    log("STATE - changeView - hull cleaning", { view: typeof nextView === "function" ? "updater" : nextView });
    setView(nextView);
  }, [setView]);

  const selectCase = useCallback((nextCase: Parameters<typeof setSelectedCase>[0]) => {
    log("STATE - selectCase - hull cleaning", { requestId: typeof nextCase === "function" ? "updater" : nextCase?.requestId ?? "none" });
    setSelectedCase(nextCase);
  }, [setSelectedCase]);

  return {
    dashboard,
    filtered,
    view,
    setView: changeView,
    filters,
    setFilter,
    clearFilters: () => {
      log("STATE - clearFilters - hull cleaning");
      setFilters(EMPTY_HULL_CLEANING_FILTERS);
    },
    selectedCase,
    setSelectedCase: selectCase,
    caseImos,
    selectCaseForImo: (imo: string) => {
      const vesselCases = dashboard?.cases.filter((row) => row.imo === imo) ?? [];
      const preferredCase = vesselCases.find(
        (row) => row.status && ["OPEN", "PLANNED"].includes(row.status.toUpperCase()),
      ) ?? null;
      setSelectedCase(preferredCase);
      log("STATE - selectCaseForImo", { imoNumber: imo, requestId: preferredCase?.requestId ?? "none" });
    },
    isLoading,
    error,
    useMockData,
  };
};
